const UUID_SEGMENT_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const HEX_TOKEN_PATTERN = /^[0-9a-f]{24,}$/i

const EMAIL_PATTERN = /[^\s/@]+@[^\s/]+\.[^\s/]+/i

const BLOCKED_EVENT_DATA_KEYS = new Set([
  'email',
  'mail',
  'username',
  'first_name',
  'last_name',
  'full_name',
  'phone',
  'password',
  'secret',
  'token',
  'access_token',
  'refresh_token',
  'authorization',
  'cookie',
  'challenge',
  'claims',
  'query',
  'search',
  'search_query',
  'text',
  'content',
  'body',
  'description',
  'message',
  'title',
  'user_id',
  'account_id',
  'identity_id',
  'tenant_id',
  'participant',
  'serial',
  'serial_number',
  'license',
  'licence',
  'license_key',
  'provider_label',
  'yaml',
])

const DYNAMIC_ROUTE_RULES: ReadonlyArray<readonly [RegExp, string]> = [
  [/^\/approvals\/(?!new\/?$)[^/]+\/?$/i, '/approvals/:id'],
  [/^\/admin\/approvals\/workflows\/[^/]+\/?$/i, '/admin/approvals/workflows/:id'],
  [/^\/personalization\/dashboards\/[^/]+\/?$/i, '/personalization/dashboards/:id'],
  [/^\/dashboards\/[^/]+\/?$/i, '/dashboards/:id'],
  [/^\/admin\/dashboards\/personal\/[^/]+\/?$/i, '/admin/dashboards/personal/:id'],
  [/^\/admin\/content\/[^/]+\/?$/i, '/admin/content/:id'],
  [/^\/admin\/dashboards\/[^/]+\/preview\/?$/i, '/admin/dashboards/:id/preview'],
  [/^\/admin\/dashboards\/[^/]+\/?$/i, '/admin/dashboards/:id'],
  [/^\/admin\/users\/[^/]+\/notifications\/?$/i, '/admin/users/:id/notifications'],
  [/^\/incidents\/[^/]+\/?$/i, '/incidents/:id'],
  [/^\/inventory\/scan\/[^/]+\/?$/i, '/inventory/scan/:token'],
  [/^\/status\/[^/]+\/?$/i, '/status/:id'],
]

export type AnalyticsSafeDataValue = string | number | boolean | null

export type AnalyticsSafeData = Record<string, AnalyticsSafeDataValue>

export type AnalyticsStatusCategory = '4xx' | '5xx' | 'network' | 'unknown'

export function analyticsStatusCategory(cause: unknown): AnalyticsStatusCategory {
  if (typeof cause !== 'object' || cause === null) return 'unknown'

  const candidate = cause as {
    status?: unknown
    statusCode?: unknown
    response?: { status?: unknown }
    name?: unknown
  }
  const status = [candidate.statusCode, candidate.status, candidate.response?.status].find(
    (value): value is number => typeof value === 'number',
  )

  if (status && status >= 400 && status < 500) return '4xx'
  if (status && status >= 500) return '5xx'
  if (candidate.name === 'FetchError' || candidate.name === 'NetworkError') return 'network'
  return 'unknown'
}

export function safeAnalyticsApplicationKey(value: unknown): string {
  if (typeof value !== 'string') return 'custom'
  const normalized = value.toLowerCase()
  if (UUID_SEGMENT_PATTERN.test(normalized)) return 'custom'
  return /^[a-z0-9][a-z0-9_-]{0,63}$/.test(normalized) ? normalized : 'custom'
}

export function analyticsCountBucket(value: unknown): '0' | '1' | '2-5' | '6+' {
  const count = Array.isArray(value) ? value.length : typeof value === 'number' ? value : 0
  if (count <= 0) return '0'
  if (count === 1) return '1'
  if (count <= 5) return '2-5'
  return '6+'
}

function safeDecode(value: string) {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function pathnameFromValue(value: string) {
  const trimmed = value.trim()

  if (!trimmed) return '/'

  try {
    return new URL(trimmed, 'https://morning.invalid').pathname || '/'
  } catch {
    const [pathname] = trimmed.split(/[?#]/, 1)
    return pathname || '/'
  }
}

function sanitizePathSegment(segment: string) {
  if (!segment) return segment

  const decoded = safeDecode(segment)

  if (UUID_SEGMENT_PATTERN.test(decoded)) return ':id'

  if (HEX_TOKEN_PATTERN.test(decoded)) return ':token'

  if (EMAIL_PATTERN.test(decoded)) return ':redacted'

  /*
   * Un segment anormalement long ressemble davantage à une donnée dynamique,
   * un token ou une valeur libre qu'à un slug Morning légitime.
   */
  if (decoded.length > 80) return ':redacted'

  return segment
}

export function normalizeAnalyticsPath(value: string) {
  let pathname = pathnameFromValue(value)

  if (!pathname.startsWith('/')) pathname = `/${pathname}`

  pathname = pathname.replace(/\/{2,}/g, '/')

  if (pathname.length > 1) pathname = pathname.replace(/\/+$/, '')

  for (const [pattern, replacement] of DYNAMIC_ROUTE_RULES) {
    if (pattern.test(pathname)) return replacement
  }

  const normalized = pathname
    .split('/')
    .map((segment, index) => (index === 0 ? '' : sanitizePathSegment(segment)))
    .join('/')

  return normalized || '/'
}

export function sanitizeAnalyticsReferrer(value: string, currentOrigin?: string) {
  const trimmed = value.trim()

  if (!trimmed) return ''

  try {
    const base = currentOrigin || 'https://morning.invalid'
    const url = new URL(trimmed, base)

    /*
     * Pour une navigation provenant de Morning, seul le chemin normalisé
     * nous intéresse.
     */
    if (
      (currentOrigin && url.origin === currentOrigin) ||
      (!currentOrigin && url.origin === 'https://morning.invalid')
    ) {
      return normalizeAnalyticsPath(url.pathname)
    }

    /*
     * Pour un site externe, on conserve uniquement l'origine.
     * Aucun pathname, query string ou fragment externe n'est envoyé.
     */
    return url.origin
  } catch {
    return ''
  }
}

function blockedEventDataKey(key: string) {
  return BLOCKED_EVENT_DATA_KEYS.has(key.trim().toLowerCase().replaceAll('-', '_'))
}

function sanitizeStringValue(value: string) {
  const trimmed = value.trim()

  if (!trimmed) return ''

  if (EMAIL_PATTERN.test(trimmed)) return '[redacted]'

  if (UUID_SEGMENT_PATTERN.test(trimmed)) return ':id'

  if (HEX_TOKEN_PATTERN.test(trimmed)) return ':token'

  if (trimmed.startsWith('/')) return normalizeAnalyticsPath(trimmed)

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      return new URL(trimmed).origin
    } catch {
      return '[redacted]'
    }
  }

  /*
   * Les propriétés Analytics ne doivent pas servir à transporter du texte libre.
   */
  if (trimmed.length > 160) return '[redacted]'

  return trimmed
}

export function sanitizeAnalyticsEventData(data: Record<string, unknown>): AnalyticsSafeData {
  const sanitized: AnalyticsSafeData = {}

  for (const [key, value] of Object.entries(data).slice(0, 50)) {
    if (!key || key.length > 60 || blockedEventDataKey(key)) continue

    if (typeof value === 'string') {
      sanitized[key] = sanitizeStringValue(value)
      continue
    }

    if (typeof value === 'number' || typeof value === 'boolean' || value === null) {
      sanitized[key] = value
    }
  }

  return sanitized
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

export function sanitizeUmamiPayload(
  payload: UmamiTrackingPayload,
  currentOrigin?: string,
): UmamiTrackingPayload | false {
  if (!isRecord(payload)) return false

  const sanitized: UmamiTrackingPayload = { ...payload }

  let normalizedUrl: string | undefined

  if (typeof sanitized.url === 'string') {
    normalizedUrl = normalizeAnalyticsPath(sanitized.url)
    sanitized.url = normalizedUrl
  }

  /*
   * document.title peut contenir des informations métier dynamiques.
   * On remplace donc systématiquement le titre transmis à Umami par
   * le chemin Analytics lorsque celui-ci est disponible.
   */
  if (normalizedUrl) {
    sanitized.title = normalizedUrl
  } else if (typeof sanitized.title === 'string') {
    sanitized.title = 'Morning'
  }

  if (typeof sanitized.referrer === 'string') {
    sanitized.referrer = sanitizeAnalyticsReferrer(sanitized.referrer, currentOrigin)
  }

  if (isRecord(sanitized.data)) {
    sanitized.data = sanitizeAnalyticsEventData(sanitized.data)
  }

  return sanitized
}
